import { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, Modal, Pagination, Popconfirm, Select, Table, Tag, Tooltip, message } from "antd";
import classNames from "classnames";
import { resourceList, resourceUpdate } from "@/apis/audit";
import { reviewSkillAudit, skillAuditPage } from "@/apis/skillhub";
import styles from "./index.module.scss";

const { TextArea } = Input;

const auditStatusOptions = [
  { label: "待审核", value: 1 },
  { label: "审核通过", value: 2 },
  { label: "审核不通过", value: 3 },
];

const runtimeOptions = [
  { label: "全部运行时", value: "" },
  { label: "Python", value: "python" },
  { label: "JavaScript", value: "javascript" },
  { label: "服务 API", value: "api" },
  { label: "Shell", value: "shell" },
];

const auditStatusMap = {
  0: { text: "未提交", color: "default" },
  1: { text: "待审核", color: "processing" },
  2: { text: "审核通过", color: "green" },
  3: { text: "审核不通过", color: "red" },
};

const normalizeRecords = (records = []) =>
  records.map((item) => ({
    ...item,
    skillUuid: item.skillUuid || item.resourceUuid,
    name: item.name || item.title,
    ownerName: item.ownerName || item.contributor?.nickname || item.contributors?.[0]?.nickname,
    createTime: item.createTime || item.auditTime,
    source: item.skillUuid ? "skillhub" : "open-resource",
  }));

const SkillHubAuditing = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [rejectRecord, setRejectRecord] = useState(null);
  const [form] = Form.useForm();
  const [params, setParams] = useState({
    auditStatus: 1,
    keywords: "",
    runtime: "",
  });
  const [page, setPage] = useState({
    currentPage: 1,
    pageSize: 10,
  });

  const fetchFallbackList = useCallback(() => {
    return resourceList({
      pageParam: page,
      auditStatus: params.auditStatus,
      keywords: params.keywords,
      firstType: null,
      secondType: "Open Skill Hub",
      resourceType: "Open Skill Hub",
    }).then((res) => {
      const result = res.data || {};
      setData(normalizeRecords(result.records || []));
      setTotal(result.total || 0);
    });
  }, [page, params.auditStatus, params.keywords]);

  const fetchList = useCallback(() => {
    setLoading(true);
    skillAuditPage({
      ...params,
      currentPage: page.currentPage,
      pageSize: page.pageSize,
    })
      .then((res) => {
        const result = res.data || {};
        setData(normalizeRecords(result.records || []));
        setTotal(result.total || 0);
      })
      .catch(() => fetchFallbackList())
      .finally(() => setLoading(false));
  }, [fetchFallbackList, page.currentPage, page.pageSize, params]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const updateAudit = (record, auditStatus, auditMessage = "") => {
    const request =
      record.source === "skillhub"
        ? reviewSkillAudit(record.skillUuid, {
            version: record.version,
            auditStatus,
            auditMessage,
            publishToDiscover: auditStatus === 2,
          })
        : resourceUpdate({
            resourceUuid: record.resourceUuid || record.skillUuid,
            auditStatus,
            auditMessage,
          });

    request.then(() => {
      message.success("审核成功");
      setRejectRecord(null);
      fetchList();
    });
  };

  const columns = [
    {
      title: "技能名称",
      dataIndex: "name",
      width: 260,
      render: (text, record) => (
        <Tooltip title={record.description}>
          <a href={`${process.env.PUBLIC_URL}/skillhub/detail?id=${record.skillUuid}`} target="_blank" rel="noreferrer">
            {text || record.skillUuid}
          </a>
        </Tooltip>
      ),
    },
    {
      title: "版本",
      dataIndex: "version",
      width: 110,
      render: (text) => text || "-",
    },
    {
      title: "运行时",
      dataIndex: "runtime",
      width: 120,
      render: (text) => text || "-",
    },
    {
      title: "上传人",
      dataIndex: "ownerName",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "审核状态",
      dataIndex: "auditStatus",
      width: 120,
      render: (value) => {
        const status = auditStatusMap[value] || auditStatusMap[1];
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: params.auditStatus === 1 ? "提交时间" : "审核时间",
      dataIndex: "createTime",
      width: 180,
      render: (text) => text || "-",
    },
    {
      title: "审核意见",
      dataIndex: "auditMessage",
      render: (text) => (
        <Tooltip title={text}>
          <span className={styles.auditMessage}>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "操作",
      width: 170,
      render: (_, record) => (
        <div className={styles.actions}>
          {[1, 2].includes(params.auditStatus) ? (
            <Button size="small" onClick={() => setRejectRecord(record)}>
              驳回
            </Button>
          ) : null}
          {[1, 3].includes(params.auditStatus) ? (
            <Popconfirm
              title="审核通过"
              description="确认通过这个 Skill 吗？"
              okText="是"
              cancelText="否"
              onConfirm={() => updateAudit(record, 2, "通过")}
            >
              <Button type="primary" size="small">
                通过
              </Button>
            </Popconfirm>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className={classNames("container", styles.auditPage)}>
      <h3>开放技能中心审核</h3>
      <div className={styles.filters}>
        <Select
          value={params.auditStatus}
          options={auditStatusOptions}
          onChange={(value) => {
            setParams({ ...params, auditStatus: value });
            setPage({ ...page, currentPage: 1 });
          }}
        />
        <Input
          allowClear
          placeholder="技能名称"
          value={params.keywords}
          onChange={(e) => setParams({ ...params, keywords: e.target.value })}
          onPressEnter={fetchList}
        />
        <Select
          value={params.runtime}
          options={runtimeOptions}
          onChange={(value) => {
            setParams({ ...params, runtime: value });
            setPage({ ...page, currentPage: 1 });
          }}
        />
        <Button type="primary" onClick={fetchList}>
          查询
        </Button>
      </div>
      <Table
        rowKey={(record) => `${record.skillUuid}-${record.version || record.createTime || ""}`}
        loading={loading}
        pagination={false}
        columns={columns}
        dataSource={data}
      />
      <Pagination
        align="end"
        className={styles.pagination}
        current={page.currentPage}
        pageSize={page.pageSize}
        total={total}
        showSizeChanger={false}
        showQuickJumper
        onChange={(currentPage, pageSize) => setPage({ currentPage, pageSize })}
      />

      <Modal
        title="驳回原因"
        width={520}
        open={Boolean(rejectRecord)}
        footer={null}
        onCancel={() => setRejectRecord(null)}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => updateAudit(rejectRecord, 3, values.auditMessage)}
        >
          <Form.Item
            name="auditMessage"
            rules={[{ required: true, message: "请输入驳回原因" }]}
          >
            <TextArea rows={4} placeholder="请输入驳回原因" />
          </Form.Item>
          <Button type="primary" htmlType="submit">
            提交
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default SkillHubAuditing;
